import { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getAuditLog, cleanupAuditLog, exportAuditLogAll } from '../../services/auditLogService'
import type { AuditLogEntry, AuditAction, AuditEntityType } from '../../types'
import type { PaginatedResponse } from '../../types'
import './AuditLogPage.css'

const AUDIT_ACTIONS: AuditAction[] = [
  'USER_CREATE', 'USER_UPDATE', 'USER_ACTIVATE', 'USER_DEACTIVATE', 'USER_ARCHIVE', 'USER_RESET_PASSWORD',
  'BRANCH_CREATE', 'BRANCH_UPDATE', 'BRANCH_APPROVE', 'BRANCH_SUSPEND', 'BRANCH_REACTIVATE', 'BRANCH_ARCHIVE',
  'ORDER_STATUS_CHANGE', 'ORDER_RETRY', 'ORDER_ARCHIVE',
]

const AUDIT_ENTITY_TYPES: AuditEntityType[] = ['USER', 'BRANCH', 'ORDER']

const ARCHIVE_ACTIONS: AuditAction[] = ['USER_ARCHIVE', 'BRANCH_ARCHIVE', 'ORDER_ARCHIVE', 'USER_DEACTIVATE']

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatLocal(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  })
}

function toDatetimeLocal(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function fromDatetimeLocal(val: string): string {
  return new Date(val).toISOString()
}

function truncate(s: string, len = 12): string {
  return s.length > len ? s.slice(0, len) + '…' : s
}

function is30DaysOld(isoFrom: string): boolean {
  const diff = Date.now() - new Date(isoFrom).getTime()
  return diff > 30 * 24 * 60 * 60 * 1000
}

function generateCsvFilename(): string {
  const now = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `audit-log_${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}.csv`
}

function escapeCsv(val: string | null | undefined): string {
  if (val == null) return ''
  return `"${val.replace(/"/g, '""')}"`
}

function downloadCsv(rows: AuditLogEntry[], filename: string) {
  const header = 'createdAt,action,entityType,entityId,adminId,ipAddress,beforeState,afterState'
  const lines = rows.map(r =>
    [r.createdAt, r.action, r.entityType, r.entityId, r.adminId, r.ipAddress ?? '', escapeCsv(r.beforeState), escapeCsv(r.afterState)].join(',')
  )
  const blob = new Blob([header + '\n' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

// ─── Diff drawer ──────────────────────────────────────────────────────────────

function DiffDrawer({ entry, onClose }: { entry: AuditLogEntry; onClose: () => void }) {
  const { t } = useTranslation()
  let before: Record<string, unknown> | null = null
  let after: Record<string, unknown> | null = null
  let beforeRaw = false
  let afterRaw = false

  if (entry.beforeState !== null) {
    try { before = JSON.parse(entry.beforeState) }
    catch { beforeRaw = true }
  }
  if (entry.afterState !== null) {
    try { after = JSON.parse(entry.afterState) }
    catch { afterRaw = true }
  }

  const allKeys = new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})])

  return (
    <div className="audit-drawer-overlay" onClick={onClose}>
      <div className="audit-drawer" onClick={e => e.stopPropagation()}>
        <div className="audit-drawer__header">
          <div className="audit-drawer__title">
            {t(`admin.audit_log.action_${entry.action}`, { defaultValue: entry.action })} — {t(`admin.audit_log.entity_${entry.entityType}`, { defaultValue: entry.entityType })} <span className="audit-drawer__entity-id">{entry.entityId}</span>
          </div>
          <button className="audit-drawer__close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="audit-drawer__meta">
          <div><span>{t('admin.audit_log.drawer_datetime')}</span><span>{formatLocal(entry.createdAt)}</span></div>
          <div><span>{t('admin.audit_log.drawer_admin')}</span><span className="audit-mono">{entry.adminId}</span></div>
          <div><span>{t('admin.audit_log.drawer_ip')}</span><span>{entry.ipAddress ?? '—'}</span></div>
        </div>

        <div className="audit-drawer__diff">
          {(beforeRaw || afterRaw) ? (
            <div className="audit-drawer__raw-section">
              {beforeRaw && (
                <div>
                  <div className="audit-drawer__raw-label">{t('admin.audit_log.before_raw')}</div>
                  <code className="audit-drawer__raw">{entry.beforeState}</code>
                </div>
              )}
              {afterRaw && (
                <div>
                  <div className="audit-drawer__raw-label">{t('admin.audit_log.after_raw')}</div>
                  <code className="audit-drawer__raw">{entry.afterState}</code>
                </div>
              )}
            </div>
          ) : allKeys.size === 0 ? (
            <div className="audit-drawer__no-data">{t('admin.audit_log.no_data')}</div>
          ) : (
            <table className="audit-diff-table">
              <thead>
                <tr>
                  <th>{t('admin.audit_log.diff_field')}</th>
                  <th>{t('admin.audit_log.diff_before')}</th>
                  <th>{t('admin.audit_log.diff_after')}</th>
                </tr>
              </thead>
              <tbody>
                {[...allKeys].map(key => {
                  const bVal = before != null && key in before ? String(before[key] ?? '') : null
                  const aVal = after != null && key in after ? String(after[key] ?? '') : null
                  const changed = bVal !== aVal
                  return (
                    <tr key={key} className={changed ? 'audit-diff-table__row--changed' : ''}>
                      <td className="audit-diff-table__key">{key}</td>
                      <td className={bVal === null ? 'audit-diff-table__dash' : ''}>{bVal ?? '—'}</td>
                      <td className={aVal === null ? 'audit-diff-table__dash' : ''}>{aVal ?? '—'}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 20
const DEFAULT_FROM = () => toDatetimeLocal(new Date(Date.now() - 24 * 60 * 60 * 1000))
const DEFAULT_TO = () => toDatetimeLocal(new Date())

export default function AuditLogPage() {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()

  const [from, setFrom] = useState(searchParams.get('from') ? searchParams.get('from')! : DEFAULT_FROM())
  const [to, setTo] = useState(searchParams.get('to') ? searchParams.get('to')! : DEFAULT_TO())
  const [action, setAction] = useState<AuditAction | ''>(searchParams.get('action') as AuditAction || '')
  const [entityType, setEntityType] = useState<AuditEntityType | ''>(searchParams.get('entityType') as AuditEntityType || '')
  const [adminId, setAdminId] = useState(searchParams.get('adminId') || '')
  const [page, setPage] = useState(Number(searchParams.get('page') || 0))

  const [result, setResult] = useState<PaginatedResponse<AuditLogEntry> | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [drawerEntry, setDrawerEntry] = useState<AuditLogEntry | null>(null)
  const [cleanupModal, setCleanupModal] = useState(false)
  const [cleanupLoading, setCleanupLoading] = useState(false)
  const [toast, setToast] = useState<string | null>(null)
  const [csvLoading, setCsvLoading] = useState(false)

  const showToast = (msg: string) => {
    setToast(msg)
    setTimeout(() => setToast(null), 4000)
  }

  const syncUrl = useCallback((overrides: Record<string, string> = {}) => {
    const params: Record<string, string> = { from, to, page: String(page) }
    if (action) params.action = action
    if (entityType) params.entityType = entityType
    if (adminId.trim()) params.adminId = adminId.trim()
    setSearchParams({ ...params, ...overrides }, { replace: true })
  }, [from, to, action, entityType, adminId, page, setSearchParams])

  const fetchData = useCallback(async (currentPage = page) => {
    setLoading(true)
    setError(null)
    try {
      const data = await getAuditLog({
        from: fromDatetimeLocal(from),
        to: fromDatetimeLocal(to),
        action: action || undefined,
        entityType: entityType || undefined,
        adminId: adminId.trim() || undefined,
        page: currentPage,
        size: PAGE_SIZE,
      })
      setResult(data)
    } catch {
      setError(t('admin.audit_log.load_failed'))
    } finally {
      setLoading(false)
    }
  }, [from, to, action, entityType, adminId, page])

  useEffect(() => {
    fetchData(page)
    syncUrl()
  }, [page]) // eslint-disable-line react-hooks/exhaustive-deps

  function handleApplyFilters() {
    setPage(0)
    fetchData(0)
    syncUrl({ page: '0' })
  }

  function handlePageChange(p: number) {
    setPage(p)
    fetchData(p)
    syncUrl({ page: String(p) })
  }

  async function handleExportCsv() {
    setCsvLoading(true)
    try {
      const rows = await exportAuditLogAll({
        from: fromDatetimeLocal(from),
        to: fromDatetimeLocal(to),
        action: action || undefined,
        entityType: entityType || undefined,
        adminId: adminId.trim() || undefined,
      })
      downloadCsv(rows, generateCsvFilename())
    } catch {
      showToast(t('admin.audit_log.csv_failed'))
    } finally {
      setCsvLoading(false)
    }
  }

  async function handleCleanup() {
    setCleanupLoading(true)
    try {
      const res = await cleanupAuditLog()
      setCleanupModal(false)
      showToast(res.deletedCount > 0 ? t('admin.audit_log.deleted_records', { count: res.deletedCount }) : t('admin.audit_log.no_to_delete'))
      if (res.deletedCount > 0) fetchData(0)
    } catch {
      showToast(t('admin.audit_log.delete_failed'))
    } finally {
      setCleanupLoading(false)
    }
  }

  const fromDate = new Date(from)
  const warnOldDate = is30DaysOld(fromDate.toISOString())

  return (
    <div className="audit-page">
      {toast && <div className="audit-toast">{toast}</div>}

      <div className="audit-page__header">
        <h1 className="audit-page__title">{t('admin.audit_log.title')}</h1>
        <div className="audit-page__actions">
          <button className="admin-btn admin-btn--secondary" onClick={handleExportCsv} disabled={csvLoading}>
            {csvLoading ? t('admin.audit_log.exporting') : t('admin.audit_log.export_csv')}
          </button>
          <button className="admin-btn admin-btn--danger-outline" onClick={() => setCleanupModal(true)}>
            {t('admin.audit_log.cleanup')}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="audit-filters">
        <div className="audit-filters__row">
          <div className="audit-filter-field">
            <label>{t('admin.audit_log.filter_from')}</label>
            <input type="datetime-local" value={from} onChange={e => setFrom(e.target.value)} className="admin-input" />
            {warnOldDate && (
              <span className="audit-filter-field__warn">
                {t('admin.audit_log.filter_warn')}
              </span>
            )}
          </div>
          <div className="audit-filter-field">
            <label>{t('admin.audit_log.filter_to')}</label>
            <input type="datetime-local" value={to} onChange={e => setTo(e.target.value)} className="admin-input" />
          </div>
          <div className="audit-filter-field">
            <label>{t('admin.audit_log.filter_action')}</label>
            <select value={action} onChange={e => setAction(e.target.value as AuditAction | '')} className="admin-input">
              <option value="">{t('admin.audit_log.filter_all')}</option>
              {AUDIT_ACTIONS.map(k => (
                <option key={k} value={k}>{t(`admin.audit_log.action_${k}`, { defaultValue: k })}</option>
              ))}
            </select>
          </div>
          <div className="audit-filter-field">
            <label>{t('admin.audit_log.filter_entity')}</label>
            <select value={entityType} onChange={e => setEntityType(e.target.value as AuditEntityType | '')} className="admin-input">
              <option value="">{t('admin.audit_log.filter_all')}</option>
              {AUDIT_ENTITY_TYPES.map(k => (
                <option key={k} value={k}>{t(`admin.audit_log.entity_${k}`, { defaultValue: k })}</option>
              ))}
            </select>
          </div>
          <div className="audit-filter-field">
            <label>{t('admin.audit_log.filter_admin')}</label>
            <input type="text" value={adminId} onChange={e => setAdminId(e.target.value)} placeholder="UUID" className="admin-input" />
          </div>
          <button className="admin-btn admin-btn--primary" onClick={handleApplyFilters}>{t('admin.audit_log.apply')}</button>
        </div>
      </div>

      {/* Table */}
      {loading && <div className="audit-skeleton">{Array.from({ length: 8 }).map((_, i) => <div key={i} className="audit-skeleton__row" />)}</div>}

      {!loading && error && (
        <div className="audit-error">
          {error}
          <button className="admin-btn admin-btn--secondary" onClick={() => fetchData(page)}>{t('admin.audit_log.retry')}</button>
        </div>
      )}

      {!loading && !error && result && result.content.length === 0 && (
        <div className="audit-empty">
          <div className="audit-empty__icon">📋</div>
          <div>{t('admin.audit_log.no_records')}</div>
        </div>
      )}

      {!loading && !error && result && result.content.length > 0 && (
        <>
          <div className="admin-table">
            <div className="admin-table__head audit-table__cols">
              <span>{t('admin.audit_log.col_datetime')}</span>
              <span>{t('admin.audit_log.col_action')}</span>
              <span>{t('admin.audit_log.col_entity_type')}</span>
              <span>{t('admin.audit_log.col_entity_id')}</span>
              <span>{t('admin.audit_log.col_admin')}</span>
              <span>{t('admin.audit_log.col_ip')}</span>
              <span>{t('admin.audit_log.col_changes')}</span>
            </div>
            {result.content.map(entry => (
              <div
                key={entry.id}
                className={`admin-table__row audit-table__cols${ARCHIVE_ACTIONS.includes(entry.action) ? ' audit-table__row--muted' : ''}`}
              >
                <span className="audit-mono">{formatLocal(entry.createdAt)}</span>
                <span>{t(`admin.audit_log.action_${entry.action}`, { defaultValue: entry.action })}</span>
                <span>{t(`admin.audit_log.entity_${entry.entityType}`, { defaultValue: entry.entityType })}</span>
                <span title={entry.entityId} className="audit-mono">{truncate(entry.entityId, 14)}</span>
                <span title={entry.adminId} className="audit-mono">{truncate(entry.adminId, 14)}</span>
                <span>{entry.ipAddress ?? '—'}</span>
                <span>
                  <button
                    className="admin-btn admin-btn--xs"
                    onClick={e => { e.stopPropagation(); setDrawerEntry(entry) }}
                  >
                    {t('admin.audit_log.details_btn')}
                  </button>
                </span>
              </div>
            ))}
          </div>

          <div className="audit-pagination">
            <span className="audit-pagination__info">
              {t('admin.audit_log.total_info', { total: result.totalElements, page: result.page + 1, pages: result.totalPages })}
            </span>
            <div className="audit-pagination__controls">
              <button
                className="admin-btn admin-btn--secondary"
                disabled={result.page === 0}
                onClick={() => handlePageChange(result.page - 1)}
              >{t('admin.audit_log.prev')}</button>
              <button
                className="admin-btn admin-btn--secondary"
                disabled={result.page + 1 >= result.totalPages}
                onClick={() => handlePageChange(result.page + 1)}
              >{t('admin.audit_log.next')}</button>
            </div>
          </div>
        </>
      )}

      {/* Detail Drawer */}
      {drawerEntry && <DiffDrawer entry={drawerEntry} onClose={() => setDrawerEntry(null)} />}

      {/* Cleanup Modal */}
      {cleanupModal && (
        <div className="audit-modal-overlay" onClick={() => setCleanupModal(false)}>
          <div className="audit-modal" onClick={e => e.stopPropagation()}>
            <h3>{t('admin.audit_log.cleanup_title')}</h3>
            <p>{t('admin.audit_log.cleanup_text')}</p>
            <div className="audit-modal__actions">
              <button className="admin-btn admin-btn--secondary" onClick={() => setCleanupModal(false)} disabled={cleanupLoading}>{t('admin.audit_log.cancel')}</button>
              <button className="admin-btn admin-btn--danger" onClick={handleCleanup} disabled={cleanupLoading}>
                {cleanupLoading ? t('admin.audit_log.deleting') : t('admin.audit_log.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
